import { brand } from '@/styles/shared';

// Container styles with responsive breakpoints and smooth backdrop animation
export const modalStyles = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: { xs: 'stretch', lg: 'center' },
  justifyContent: 'center',
  padding: { xs: 0, lg: 2 },
  zIndex: 1300,
  // Smooth backdrop fade
  animation: 'backdropFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes backdropFadeIn': {
    '0%': {
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
    '100%': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
  },
} as const;

export const contentStyles = {
  backgroundColor: '#ffffff',
  borderRadius: { xs: 0, lg: '16px' },
  boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
  width: '100%',
  maxWidth: { xs: 'none', lg: '400px' },
  height: { xs: '100%', lg: 'auto' },
  maxHeight: { xs: 'none', lg: '95vh' },
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
  // Natural spring-like entrance animation
  animation: {
    xs: 'slideUpIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
    lg: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  '@keyframes slideUpIn': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
  '@keyframes scaleIn': {
    '0%': {
      transform: 'scale(0.9)',
      opacity: 0,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
} as const;

export const headerStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: { xs: '12px 16px 6px 16px', lg: '16px 20px 8px 20px' },
  borderBottom: { xs: '1px solid #e0e0e0', lg: 'none' },
} as const;

export const contentAreaStyles = {
  padding: { xs: '12px 16px 16px 16px', lg: '16px 20px 20px 20px' },
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
} as const;

export const patientIdDisplayStyles = {
  width: '100%',
  maxWidth: '500px',
  margin: { xs: '0px auto 16px auto', lg: '0px auto 24px auto' },
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  backgroundColor: '#f8f9fa',
  minHeight: { xs: '48px', lg: '60px' },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
} as const;

export const clearTextButtonStyles = {
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  minWidth: 'auto',
  padding: '4px 8px',
  fontSize: '12px',
  textTransform: 'none',
  color: '#666666',
  borderRadius: '6px',
  backgroundColor: 'transparent',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: '#ececec',
    transform: 'translateY(-50%) scale(1.05)',
  },
  '&:active': {
    transform: 'translateY(-50%) scale(0.95)',
    transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

export const keypadButtonStyles = {
  width: { xs: '59px', lg: '64px' },
  height: { xs: '59px', lg: '64px' },
  minWidth: { xs: '59px', lg: '64px' },
  minHeight: { xs: '59px', lg: '64px' },
  borderRadius: '50%',
  backgroundColor: '#f5f5f5',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: { xs: '20px', lg: '24px' },
  fontWeight: 600,
  color: '#333333',
  cursor: 'pointer',
  // Native-feeling transitions with different properties
  transition:
    'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  transformOrigin: 'center',
  '&:hover': {
    backgroundColor: '#e8e8e8',
    transform: 'scale(1.02)',
  },
  '&:active': {
    backgroundColor: brand.purpleAccent,
    color: '#ffffff',
    transform: 'scale(0.96)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    transition:
      'background-color 0.1s cubic-bezier(0.4, 0, 0.2, 1), transform 0.1s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    transform: 'none',
    color: 'rgba(51, 51, 51, 0.5)',
  },
} as const;

const actionButtonStyles = {
  width: { xs: '59px', lg: '64px' },
  height: { xs: '59px', lg: '64px' },
  minWidth: { xs: '59px', lg: '64px' },
  minHeight: { xs: '59px', lg: '64px' },
  borderRadius: '50%',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  // Smooth, native-feeling transitions
  transition:
    'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  transformOrigin: 'center',
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    transform: 'none',
  },
} as const;

export const deleteButtonStyles = {
  ...actionButtonStyles,
  backgroundColor: '#f5f5f5',
  minWidth: '64px',
  width: '64px',
  minHeight: '64px',
  height: '64px',
  color: '#666666',
  '&:hover': {
    backgroundColor: '#e8e8e8',
    transform: 'scale(1.02)',
  },
  '&:active': {
    backgroundColor: '#ffebee',
    transform: 'scale(0.96)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition:
      'background-color 0.1s cubic-bezier(0.4, 0, 0.2, 1), transform 0.1s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&:disabled': {
    backgroundColor: '#f5f5f5',
    color: '#cccccc',
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
  },
} as const;

export const submitButtonStyles = {
  ...actionButtonStyles,
  backgroundColor: brand.purpleAccent,
  minWidth: '64px',
  width: '64px',
  minHeight: '64px',
  height: '64px',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: brand.purpleAccentDark,
    transform: 'scale(1.02)',
    boxShadow: '0 4px 12px rgba(139, 69, 195, 0.3)',
  },
  '&:active': {
    backgroundColor: '#4a0e66',
    transform: 'scale(0.96)',
    boxShadow: '0 2px 8px rgba(139, 69, 195, 0.4)',
    transition:
      'background-color 0.1s cubic-bezier(0.4, 0, 0.2, 1), transform 0.1s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#888888',
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
} as const;
